// Standard Calyvra — New Client intake (full demographic profile, no Athena ID).
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { ArrowLeft, Loader2, UserPlus, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const SOURCES = ['Direct', 'Referral', 'External Agent', 'Partner Referral', 'Other'];
const SEXES = ['Male', 'Female', 'Other', 'Prefer not to say'];
const LANGS = ['English', 'Spanish', 'Other'];
const MARITAL = ['Single or legally divorced', 'Married'];

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl p-6 border border-slate-200">
    <h3 className="font-semibold text-slate-900 mb-4">{title}</h3>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </div>
);
const F = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-slate-600">{label}</Label>{children}</div>
);

export function StandardClientIntakePage() {
  const navigate = useNavigate();
  const { activeWorkspace, workspaces } = useAuth();
  const [constants, setConstants] = useState(null);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [dupeChecked, setDupeChecked] = useState(false);
  const [f, setF] = useState({
    first_name: '', middle_name: '', last_name: '', date_of_birth: '', sex: '', preferred_language: 'English',
    phone: '', email: '', street_address: '', apartment: '', city: '', state: '', zip_code: '', county: '',
    ssn: '', immigration_status: '', currently_smokes: '', marital_status: '', occupation: '',
    income_period: 'annual', income_amount: '', will_file_taxes: '', source: 'Direct', primary_agent_id: '',
    spouse_first_name: '', spouse_last_name: '', spouse_date_of_birth: '', spouse_occupation: '',
    spouse_income: '', spouse_ssn: '', file_jointly: '',
  });

  const stdWs = activeWorkspace?.client_profile === 'simplified'
    ? workspaces.find((w) => w.client_profile === 'full')
    : activeWorkspace;

  useEffect(() => { portalApi.getConstants().then(setConstants).catch(() => {}); portalApi.getAgents().then(setAgents).catch(() => {}); }, []);

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setDupeChecked(false); };
  const isMarried = f.marital_status === 'Married';

  const runDuplicateCheck = async () => {
    const res = await portalApi.checkClientDuplicates({
      first_name: f.first_name, last_name: f.last_name,
      date_of_birth: f.date_of_birth, email: f.email, phone: f.phone,
    });
    setDupes(res.matches || []);
    setDupeChecked(true);
    return res.matches || [];
  };

  const submit = async () => {
    if (!f.first_name || !f.last_name) { toast.error('First and last name are required'); return; }
    if (!stdWs) { toast.error('No Standard Calyvra workspace available'); return; }
    // Duplicate warning gate: check once; require an explicit second confirmation if matches found
    if (!dupeChecked) {
      const matches = await runDuplicateCheck();
      if (matches.length > 0) {
        toast.warning('Potential duplicate client(s) found. Review below, then click Create again to proceed.');
        return;
      }
    }
    setSaving(true);
    try {
      const demographics = {
        first_name: f.first_name, middle_name: f.middle_name || null, last_name: f.last_name,
        date_of_birth: f.date_of_birth || null, sex: f.sex || null, preferred_language: f.preferred_language,
        phone: f.phone || null, email: f.email || null,
        street_address: f.street_address || null, apartment: f.apartment || null, city: f.city || null,
        state: f.state || null, zip_code: f.zip_code || null, county: f.county || null,
        ssn: f.ssn || null, immigration_status: f.immigration_status || null,
        currently_smokes: f.currently_smokes === '' ? null : f.currently_smokes === 'yes',
        marital_status: f.marital_status || null, occupation: f.occupation || null,
        income_period: f.income_period, income_amount: f.income_amount ? parseFloat(f.income_amount) : null,
        will_file_taxes: f.will_file_taxes === '' ? null : f.will_file_taxes === 'yes',
        spouse: isMarried ? {
          first_name: f.spouse_first_name || null, last_name: f.spouse_last_name || null,
          date_of_birth: f.spouse_date_of_birth || null, occupation: f.spouse_occupation || null,
          income: f.spouse_income ? parseFloat(f.spouse_income) : null, ssn: f.spouse_ssn || null,
          file_jointly: f.file_jointly === '' ? null : f.file_jointly === 'yes',
        } : null,
      };
      const client = await portalApi.createClient({ workspace_id: stdWs.id, source: f.source, primary_agent_id: f.primary_agent_id || null, demographics });
      toast.success(`Client ${client.calyvra_client_id} created`);
      navigate(`/portal/clients/${client.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="standard-client-intake">
      <button onClick={() => navigate('/portal/clients')} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm"><ArrowLeft className="h-4 w-4" /> Back to Clients</button>
      <div className="flex items-center gap-2">
        <UserPlus className="h-6 w-6 text-sky-500" />
        <h1 className="text-2xl font-bold text-slate-900">New Client — Standard Calyvra</h1>
      </div>
      <p className="text-sm text-slate-500 -mt-4">A permanent Calyvra Client ID is assigned automatically. Athena ID does not apply here.</p>

      {dupeChecked && dupes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="duplicate-warning">
          <div className="flex items-center gap-2 text-amber-800 font-medium mb-2"><AlertTriangle className="h-4 w-4" /> Potential duplicate client(s)</div>
          <div className="space-y-1">
            {dupes.map((m) => (
              <div key={m.id} className="text-sm text-amber-900 flex items-center gap-2 flex-wrap">
                <a href={`/portal/clients/${m.id}`} target="_blank" rel="noreferrer" className="font-medium underline">{m.calyvra_client_id}</a>
                <span>{m.name}</span>{m.date_of_birth && <span>· DOB {m.date_of_birth}</span>}
                {m.email && <span>· {m.email}</span>}{m.active_workspace && <span>· {m.active_workspace}</span>}
                <Button size="sm" variant="outline" className="ml-auto h-7 border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => navigate(`/portal/clients/${m.id}`)} data-testid={`use-existing-client-${m.calyvra_client_id}`}>Use existing client</Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-700 mt-2">Same person already in another group? Open their record and use <strong>“Add to Group”</strong> to add them here instead of creating a duplicate. Otherwise click “Create Client” again to proceed.</p>
        </div>
      )}

      <Section title="Client Information">
        <F label="First Name *"><Input value={f.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="intake-first-name" /></F>
        <F label="Middle Name"><Input value={f.middle_name} onChange={(e) => set('middle_name', e.target.value)} /></F>
        <F label="Last Name *"><Input value={f.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="intake-last-name" /></F>
        <F label="Date of Birth"><Input type="date" value={f.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} data-testid="intake-dob" /></F>
        <F label="Sex"><Select value={f.sex} onValueChange={(v) => set('sex', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{SEXES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></F>
        <F label="Preferred Language"><Select value={f.preferred_language} onValueChange={(v) => set('preferred_language', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{LANGS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></F>
      </Section>

      <Section title="Contact Information">
        <F label="Phone Number"><Input value={f.phone} onChange={(e) => set('phone', e.target.value)} data-testid="intake-phone" /></F>
        <F label="Email Address"><Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} data-testid="intake-email" /></F>
      </Section>

      <Section title="Address">
        <F label="Street Address"><Input value={f.street_address} onChange={(e) => set('street_address', e.target.value)} /></F>
        <F label="Apartment / Unit"><Input value={f.apartment} onChange={(e) => set('apartment', e.target.value)} /></F>
        <F label="City"><Input value={f.city} onChange={(e) => set('city', e.target.value)} /></F>
        <F label="State"><Select value={f.state} onValueChange={(v) => set('state', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{(constants?.us_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></F>
        <F label="ZIP Code"><Input value={f.zip_code} onChange={(e) => set('zip_code', e.target.value)} /></F>
        <F label="County"><Input value={f.county} onChange={(e) => set('county', e.target.value)} /></F>
      </Section>

      <Section title="Additional Enrollment Information">
        <F label="Social Security Number"><Input value={f.ssn} onChange={(e) => set('ssn', e.target.value)} placeholder="XXX-XX-XXXX" data-testid="intake-ssn" /></F>
        <F label="Immigration Status"><Select value={f.immigration_status} onValueChange={(v) => set('immigration_status', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{(constants?.immigration_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></F>
        <F label="Currently Smokes"><Select value={f.currently_smokes} onValueChange={(v) => set('currently_smokes', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select></F>
        <F label="Marital Status"><Select value={f.marital_status} onValueChange={(v) => set('marital_status', v)}><SelectTrigger data-testid="intake-marital"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{MARITAL.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></F>
        <F label="Occupation"><Input value={f.occupation} onChange={(e) => set('occupation', e.target.value)} /></F>
        <F label="Income Period"><Select value={f.income_period} onValueChange={(v) => set('income_period', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent></Select></F>
        <F label="Estimated Income (before taxes)"><Input type="number" value={f.income_amount} onChange={(e) => set('income_amount', e.target.value)} /></F>
        <F label="Plans to file taxes this year"><Select value={f.will_file_taxes} onValueChange={(v) => set('will_file_taxes', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select></F>
        <F label="Source"><Select value={f.source} onValueChange={(v) => set('source', v)}><SelectTrigger data-testid="intake-source"><SelectValue /></SelectTrigger><SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></F>
        <F label="Primary Agent / Producer"><Select value={f.primary_agent_id} onValueChange={(v) => set('primary_agent_id', v)}><SelectTrigger data-testid="intake-primary-agent"><SelectValue placeholder="Select agent" /></SelectTrigger><SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></F>
      </Section>

      {isMarried && (
        <Section title="Spouse Information">
          <F label="Spouse First Name"><Input value={f.spouse_first_name} onChange={(e) => set('spouse_first_name', e.target.value)} data-testid="intake-spouse-first" /></F>
          <F label="Spouse Last Name"><Input value={f.spouse_last_name} onChange={(e) => set('spouse_last_name', e.target.value)} /></F>
          <F label="Spouse Date of Birth"><Input type="date" value={f.spouse_date_of_birth} onChange={(e) => set('spouse_date_of_birth', e.target.value)} /></F>
          <F label="Spouse Occupation"><Input value={f.spouse_occupation} onChange={(e) => set('spouse_occupation', e.target.value)} /></F>
          <F label="Spouse Income"><Input type="number" value={f.spouse_income} onChange={(e) => set('spouse_income', e.target.value)} /></F>
          <F label="Spouse SSN (if applicable)"><Input value={f.spouse_ssn} onChange={(e) => set('spouse_ssn', e.target.value)} /></F>
          <F label="Filing Taxes Jointly"><Select value={f.file_jointly} onValueChange={(v) => set('file_jointly', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select></F>
        </Section>
      )}

      <div className="flex justify-end gap-2 pb-8">
        <Button variant="outline" onClick={() => navigate('/portal/clients')}>Cancel</Button>
        <Button onClick={submit} disabled={saving} data-testid="submit-client-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {dupeChecked && dupes.length > 0 ? 'Create Client Anyway' : 'Create Client'}
        </Button>
      </div>
    </div>
  );
}

export default StandardClientIntakePage;
